import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TableSortLabel
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Download, Refresh } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import { useSelector } from 'react-redux';
import Papa from 'papaparse';
import DatePicker from '../../../components/DatePicker';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import axios from '../../../resources/axios';
import { selectCities } from '../../../redux/slices/citiesSlice';
import { selectAdmins } from '../../../redux/slices/adminsSlice';
import { selectAuth } from '../../../redux/slices/authSlice';

const CITY_SHORT_MAP: Record<string, string> = {
  '5ff99e7d863d42769b05c486': 'IF',
  '60d59e08bd31e836ae204610': 'VN',
  '60058dc791cd964825147a6a': 'ZT',
  '63f7b73cfcb1a10e71e62078': 'BT',
  '63fc94c0fcb1a10e71e620b7': 'KL',
  '66280975a8831e51b380e3cd': 'CHR',
  '66a2533d5f572298529446b8': 'VSK',
  '68721a559e0fad9ff9ad52a9': 'KAL'
};

const getCityShort = (cityId: string): string => {
  return CITY_SHORT_MAP[cityId] || cityId;
};

interface WorkerActivityData {
  login: string;
  transportingCount: number;
  chargingCount: number;
  availableCount: number;
  total: number;
  lastAction: string;
}

interface CSVRow {
  login?: string;
  TRANSPORTINGid?: string;
  TRANSPORTINGLocation?: string;
  TRANSPORTINGTime?: string;
  CHARGINGid?: string;
  CHARGINGLocation?: string;
  CHARGINGTime?: string;
  AVAILABLEid?: string;
  AVAILABLELocation?: string;
  AVAILABLETime?: string;
}

const WorkerActivityTab = () => {
  const theme = useTheme();
  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);

  // Автоматично встановлюємо останні 15 днів до вчорашнього дня
  const [dateFrom, setDateFrom] = useState<Moment | null>(moment().subtract(1, 'day').subtract(15, 'days'));
  const [dateTo, setDateTo] = useState<Moment | null>(moment().subtract(1, 'day'));
  const [loading, setLoading] = useState(false);
  const [workerData, setWorkerData] = useState<WorkerActivityData[]>([]);
  const [visibleStatuses, setVisibleStatuses] = useState({
    transportingCount: true,
    chargingCount: true,
    availableCount: true
  });
  const [sortBy, setSortBy] = useState<keyof WorkerActivityData>('total');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Фільтруємо міста за доступом адміна
  const availableCities = useMemo(() => {
    if (auth.info?.type === 'superAdmin') {
      return cities.items;
    }
    if (adminInfo.info?.cities) {
      return adminInfo.info.cities;
    }
    return [];
  }, [auth.info?.type, adminInfo.info?.cities, cities.items]);

  const currentCity = useMemo(() => {
    return availableCities.find(item => item._id === adminInfo.currentCity);
  }, [adminInfo.currentCity, availableCities]);

  // Автоматично використовуємо обране місто з бокового меню
  const selectedCityShort = useMemo(() => {
    if (currentCity) {
      return getCityShort(currentCity._id);
    }
    return availableCities.length > 0 ? getCityShort(availableCities[0]._id) : '';
  }, [currentCity, availableCities]);

  const aggregateData = useCallback((csvData: string): WorkerActivityData[] => {
    const parsed = Papa.parse<CSVRow>(csvData, { 
      header: true,
      skipEmptyLines: true
    });

    const workerMap = new Map<string, {
      transportingCount: number;
      chargingCount: number;
      availableCount: number;
      lastActionTime: number;
    }>();

    parsed.data.forEach(row => {
      if (!row.login) return;

      const login = row.login;
      if (!workerMap.has(login)) {
        workerMap.set(login, {
          transportingCount: 0,
          chargingCount: 0,
          availableCount: 0,
          lastActionTime: 0
        });
      }

      const worker = workerMap.get(login)!;

      if (row.TRANSPORTINGid) {
        worker.transportingCount++;
        if (row.TRANSPORTINGTime) {
          const time = moment(row.TRANSPORTINGTime).valueOf();
          if (time > worker.lastActionTime) {
            worker.lastActionTime = time;
          }
        }
      }

      if (row.CHARGINGid) {
        worker.chargingCount++;
        if (row.CHARGINGTime) {
          const time = moment(row.CHARGINGTime).valueOf();
          if (time > worker.lastActionTime) {
            worker.lastActionTime = time;
          }
        }
      }

      if (row.AVAILABLEid) {
        worker.availableCount++;
        if (row.AVAILABLETime) {
          const time = moment(row.AVAILABLETime).valueOf();
          if (time > worker.lastActionTime) {
            worker.lastActionTime = time;
          }
        }
      }
    });

    const result: WorkerActivityData[] = [];
    workerMap.forEach((data, login) => {
      result.push({
        login,
        transportingCount: data.transportingCount,
        chargingCount: data.chargingCount,
        availableCount: data.availableCount,
        total: data.transportingCount + data.chargingCount + data.availableCount,
        lastAction: data.lastActionTime > 0 
          ? moment(data.lastActionTime).format('DD.MM.YYYY HH:mm') 
          : '-'
      });
    });

    return result.sort((a, b) => b.total - a.total);
  }, []);

  const fetchAnalytics = useCallback(async () => {
    if (!dateFrom || !dateTo || !selectedCityShort) return;

    setLoading(true);
    try {
      const dateFromStr = dateFrom.format('YYYY-MM-DD');
      const dateToStr = dateTo.format('YYYY-MM-DD');
      const url = `/statistics/status_updates_for_ai_analyze?dateFrom=${dateFromStr}&dateTo=${dateToStr}&cityShort=${selectedCityShort}`;
      
      console.log('Fetching worker activity from:', url);
      console.log('Params:', { dateFrom: dateFromStr, dateTo: dateToStr, cityShort: selectedCityShort });
      
      const response = await axios.get(url, {
        responseType: 'text'
      });

      console.log('Response data type:', typeof response.data);
      console.log('Response data length:', response.data?.length);
      console.log('Response data preview:', response.data?.substring(0, 200));

      const aggregated = aggregateData(response.data);
      console.log('Aggregated data:', aggregated);
      setWorkerData(aggregated);
    } catch (error) {
      console.error('Error fetching worker activity:', error);
      setWorkerData([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedCityShort, aggregateData]);

  // Обробник зміни дати "Від" з валідацією макс 15 днів
  const handleDateFromChange = useCallback((newDate: Moment | null) => {
    if (!newDate) {
      setDateFrom(newDate);
      return;
    }
    
    if (dateTo) {
      const daysDiff = dateTo.diff(newDate, 'days');
      if (daysDiff > 15) {
        // Якщо період більше 15 днів, автоматично коригуємо dateTo
        setDateTo(newDate.clone().add(15, 'days'));
      }
    }
    setDateFrom(newDate);
  }, [dateTo]);

  // Обробник зміни дати "До" з валідацією макс 15 днів
  const handleDateToChange = useCallback((newDate: Moment | null) => {
    if (!newDate) {
      setDateTo(newDate);
      return;
    }
    
    if (dateFrom) {
      const daysDiff = newDate.diff(dateFrom, 'days');
      if (daysDiff > 15) {
        // Якщо період більше 15 днів, автоматично коригуємо dateFrom
        setDateFrom(newDate.clone().subtract(15, 'days'));
      }
    }
    setDateTo(newDate);
  }, [dateFrom]);

  const handleDownloadCSV = useCallback(() => {
    if (!dateFrom || !dateTo || !selectedCityShort) return;

    const dateFromStr = dateFrom.format('YYYY-MM-DD');
    const dateToStr = dateTo.format('YYYY-MM-DD');
    const url = `/api/statistics/status_updates_for_ai_analyze?dateFrom=${dateFromStr}&dateTo=${dateToStr}&cityShort=${selectedCityShort}`;
    window.open(url, '_blank');
  }, [dateFrom, dateTo, selectedCityShort]);

  const handleLegendClick = useCallback((dataKey: string) => {
    console.log('Legend clicked, dataKey:', dataKey);
    setVisibleStatuses(prev => {
      const newState = {
        ...prev,
        [dataKey]: !prev[dataKey as keyof typeof prev]
      };
      console.log('New visibleStatuses:', newState);
      return newState;
    });
  }, []);

  const handleSort = useCallback((field: keyof WorkerActivityData) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  }, [sortBy, sortDirection]);

  const sortedWorkerData = useMemo(() => {
    return [...workerData].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'lastAction') {
        if (aValue === '-') aValue = 0;
        if (bValue === '-') bValue = 0;
        if (typeof aValue === 'string') aValue = moment(aValue, 'DD.MM.YYYY HH:mm').valueOf();
        if (typeof bValue === 'string') bValue = moment(bValue, 'DD.MM.YYYY HH:mm').valueOf();
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }

      return 0;
    });
  }, [workerData, sortBy, sortDirection]);

  const renderLegend = useCallback((props: any) => {
    const { payload } = props;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, paddingTop: '10px' }}>
        {payload.map((entry: any) => {
          const dataKey = entry.dataKey;
          const isVisible = visibleStatuses[dataKey as keyof typeof visibleStatuses];
          
          return (
            <Box
              key={entry.value}
              onClick={() => handleLegendClick(dataKey)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                opacity: isVisible ? 1 : 0.4,
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: isVisible ? 0.8 : 0.6
                }
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  backgroundColor: entry.color,
                  borderRadius: '2px'
                }}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  userSelect: 'none'
                }}
              >
                {entry.value}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }, [visibleStatuses, handleLegendClick, theme.palette.mode]);

  return (
    <Box>
      <Paper
        sx={{
          padding: '20px',
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
          borderRadius: 2,
          mb: 3
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 18,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
            mb: 1
          }}
        >
          Активність працівників
        </Typography>

        <Typography
          sx={{
            fontSize: 14,
            color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
            mb: 3
          }}
        >
          📊 Для генерації звіту оберіть дату "Від" і "До", потім натисніть "Оновити аналітику". Максимальний період: 15 днів. Місто: {currentCity?.cityName || 'Не обрано'}
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ minWidth: 150 }}>
            <DatePicker
              label="Від"
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </Box>

          <Box sx={{ minWidth: 150 }}>
            <DatePicker
              label="До"
              value={dateTo}
              onChange={handleDateToChange}
            />
          </Box>

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={fetchAnalytics}
            disabled={loading || !dateFrom || !dateTo || !selectedCityShort}
            sx={{
              backgroundColor: '#5073F8',
              '&:hover': { backgroundColor: '#3d5dd6' }
            }}
          >
            Оновити аналітику
          </Button>

          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadCSV}
            disabled={!dateFrom || !dateTo || !selectedCityShort}
            sx={{
              borderColor: '#878787',
              color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
              '&:hover': { borderColor: '#616161' }
            }}
          >
            Завантажити CSV
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : workerData.length === 0 ? (
        <Paper
          sx={{
            padding: '40px',
            textAlign: 'center',
            border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
            borderRadius: 2
          }}
        >
          <Typography
            sx={{
              color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
              fontSize: 16
            }}
          >
            За обраний період активності не знайдено.
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper
            sx={{
              padding: '20px',
              border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
              borderRadius: 2,
              mb: 3
            }}
          >
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
                mb: 2
              }}
            >
              Графік активності
            </Typography>

            <Box sx={{ width: '100%', height: 400 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                  data={workerData.slice(0, 15)}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke={theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}
                  />
                  <XAxis
                    dataKey='login'
                    angle={-45}
                    textAnchor='end'
                    height={100}
                    tick={{
                      fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                      fontSize: 12
                    }}
                  />
                  <YAxis
                    tick={{
                      fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                      fontSize: 12
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#424242',
                      border: `1px solid ${theme.palette.mode === 'light' ? '#E0E0E0' : '#616161'}`,
                      borderRadius: 4
                    }}
                  />
                  <Legend content={renderLegend} />
                  {visibleStatuses.transportingCount && (
                    <Bar
                      dataKey='transportingCount'
                      name='На перевезенні'
                      fill='#FF9800'
                      stackId='a'
                    />
                  )}
                  {visibleStatuses.chargingCount && (
                    <Bar
                      dataKey='chargingCount'
                      name='На зарядці'
                      fill='#9C27B0'
                      stackId='a'
                    />
                  )}
                  {visibleStatuses.availableCount && (
                    <Bar
                      dataKey='availableCount'
                      name='Доступний'
                      fill='#4CAF50'
                      stackId='a'
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <TableContainer
            component={Paper}
            sx={{
              border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
              borderRadius: 2
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A'
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>Працівник</TableCell>
                  <TableCell align='center' sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortBy === 'transportingCount'}
                      direction={sortBy === 'transportingCount' ? sortDirection : 'desc'}
                      onClick={() => handleSort('transportingCount')}
                    >
                      На перевезенні
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='center' sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortBy === 'chargingCount'}
                      direction={sortBy === 'chargingCount' ? sortDirection : 'desc'}
                      onClick={() => handleSort('chargingCount')}
                    >
                      На зарядці
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='center' sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortBy === 'availableCount'}
                      direction={sortBy === 'availableCount' ? sortDirection : 'desc'}
                      onClick={() => handleSort('availableCount')}
                    >
                      Доступний
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='center' sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortBy === 'total'}
                      direction={sortBy === 'total' ? sortDirection : 'desc'}
                      onClick={() => handleSort('total')}
                    >
                      Усього
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortBy === 'lastAction'}
                      direction={sortBy === 'lastAction' ? sortDirection : 'desc'}
                      onClick={() => handleSort('lastAction')}
                    >
                      Остання активність
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedWorkerData.map((worker) => (
                  <TableRow
                    key={worker.login}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(78, 205, 196, 0.2)'
                      }
                    }}
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>
                        {worker.login}
                      </Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '12px',
                          backgroundColor: '#FF980033',
                          color: '#FF9800',
                          fontWeight: 600
                        }}
                      >
                        {worker.transportingCount}
                      </Box>
                    </TableCell>
                    <TableCell align='center'>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '12px',
                          backgroundColor: '#9C27B033',
                          color: '#9C27B0',
                          fontWeight: 600
                        }}
                      >
                        {worker.chargingCount}
                      </Box>
                    </TableCell>
                    <TableCell align='center'>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '12px',
                          backgroundColor: '#4CAF5033',
                          color: '#4CAF50',
                          fontWeight: 600
                        }}
                      >
                        {worker.availableCount}
                      </Box>
                    </TableCell>
                    <TableCell align='center'>
                      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                        {worker.total}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Typography
                        sx={{
                          color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                          fontSize: 14
                        }}
                      >
                        {worker.lastAction}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default WorkerActivityTab;
